/**
 * SiteFeedback.jsx — Site-level feedback form (NPS + Sean Ellis PMF)
 *
 * Captures product-level feedback about LocalGenie itself, distinct from
 * neighborhood reviews (/submit-review) and per-recommendation thumbs.
 *
 * Linked from the SplashScreen footer ("Share feedback").
 *
 * Form structure (deliberately short — anti-overwhelm):
 *   1. NPS 0-10                         (required)
 *   2. Sean Ellis PMF                   (optional)
 *   3. Open comment                     (optional)
 *   4. Email                            (optional, for follow-up)
 *
 * Success state replaces the form with a thank-you panel + link back home.
 *
 * Created 2026-05-03 (Sunday polish — pre-launch reaction capture).
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { submitSiteFeedback } from '../services/siteFeedbackApi';
import LogoIcon from '../components/Brand/LogoIcon';
import './SiteFeedback.css';

// Note: the inline LogoIcon was moved to components/Brand/LogoIcon.jsx
// (Sunday polish 2026-05-03 round 2) so all pages share one source of
// truth. Updating the brand mark is a single-file change now.

// PMF (Sean Ellis) options, ordered most → least disappointed plus an opt-out.
const PMF_OPTIONS = [
  { value: 'very_disappointed',     label: 'Very disappointed' },
  { value: 'somewhat_disappointed', label: 'Somewhat disappointed' },
  { value: 'not_disappointed',      label: 'Not disappointed' },
  { value: 'havent_tried',          label: "I haven't really tried it" }
];

// NPS button rail — 0..10. Rendered as a row of small chips.
const NPS_VALUES = Array.from({ length: 11 }, (_, i) => i);

const SiteFeedback = () => {
  // Round 4 (LG feedback): Cancel should return to wherever the user came
  // from (chat or splash), not a hardcoded route. navigate(-1) walks one
  // step back in the browser history — exactly the "back" semantics LG
  // expected. If history is empty (deep-link), fall back to /.
  const navigate = useNavigate();
  const goBack = () => {
    // window.history.length includes the current entry; > 1 means there
    // is at least one prior page to return to.
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const [npsScore, setNpsScore]       = useState(null);
  const [pmfResponse, setPmfResponse] = useState('');
  const [comment, setComment]         = useState('');
  const [email, setEmail]             = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState(null);
  const [submitted, setSubmitted]   = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (npsScore === null) {
      setError('Please pick a score from 0 to 10 before submitting.');
      return;
    }

    setSubmitting(true);

    // Pull the same sessionId AIChat uses, so site feedback can later be
    // correlated with usage events if we want that view.
    const sessionId = (() => {
      try { return localStorage.getItem('ai_session_id') || null; }
      catch { return null; }
    })();

    const payload = {
      npsScore,
      pmfResponse: pmfResponse || undefined,
      comment:     comment.trim() || undefined,
      email:       email.trim() || undefined,
      sessionId
    };

    const result = await submitSiteFeedback(payload);
    setSubmitting(false);

    if (result.success) {
      setSubmitted(true);
    } else {
      setError(result.error || 'Something went wrong. Please try again.');
    }
  };

  return (
    <div className="sf-page">
      {/* ─── Top nav (matches splash style) ─────────────────────────── */}
      <header className="sf-nav-wrap">
        <nav className="sf-nav">
          <Link to="/" className="sf-logo">
            <LogoIcon size={28} />
            <span className="sf-logo-text">
              LocalGenie<span className="sf-logo-accent">.ai</span>
            </span>
          </Link>
          <Link to="/home" className="sf-nav-cta">
            Try LocalGenie →
          </Link>
        </nav>
      </header>

      <main className="sf-main">
        {!submitted ? (
          <form className="sf-card" onSubmit={handleSubmit} noValidate>
            <div className="sf-card-header">
              <h1 className="sf-card-title">How are we doing?</h1>
              <p className="sf-card-subtitle">
                A quick read on whether LocalGenie is useful — your reply
                shapes what we build next. Just one required question.
              </p>
            </div>

            {/* ─── NPS (required) ───────────────────────────────────── */}
            <fieldset className="sf-field">
              <legend className="sf-field-label">
                How likely are you to recommend LocalGenie to a friend?
                <span className="sf-required">*</span>
              </legend>

              <div className="sf-nps-row" role="radiogroup" aria-label="NPS score">
                {NPS_VALUES.map((n) => (
                  <button
                    type="button"
                    key={n}
                    role="radio"
                    aria-checked={npsScore === n}
                    className={
                      'sf-nps-chip' +
                      (npsScore === n ? ' sf-nps-chip-active' : '')
                    }
                    onClick={() => setNpsScore(n)}
                  >
                    {n}
                  </button>
                ))}
              </div>

              <div className="sf-nps-anchors">
                <span>Not at all likely</span>
                <span>Extremely likely</span>
              </div>
            </fieldset>

            {/* ─── PMF (Sean Ellis, optional) ───────────────────────── */}
            <fieldset className="sf-field">
              <legend className="sf-field-label">
                If LocalGenie went away tomorrow, how would you feel?
                <span className="sf-optional"> · optional</span>
              </legend>

              <div className="sf-pmf-options">
                {PMF_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className={
                      'sf-pmf-option' +
                      (pmfResponse === opt.value ? ' sf-pmf-option-active' : '')
                    }
                  >
                    <input
                      type="radio"
                      name="pmf"
                      value={opt.value}
                      checked={pmfResponse === opt.value}
                      onChange={() => setPmfResponse(opt.value)}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            {/* ─── Comment (optional) ───────────────────────────────── */}
            <div className="sf-field">
              <label className="sf-field-label" htmlFor="sf-comment">
                What's working? What could be better?
                <span className="sf-optional"> · optional</span>
              </label>
              <textarea
                id="sf-comment"
                className="sf-textarea"
                rows={4}
                maxLength={1000}
                placeholder="Anything that surprised you, frustrated you, or you wish was different..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
              <div className="sf-charcount">
                {comment.length} / 1000
              </div>
            </div>

            {/* ─── Email (optional) ─────────────────────────────────── */}
            <div className="sf-field">
              <label className="sf-field-label" htmlFor="sf-email">
                Email
                <span className="sf-optional"> · optional, only if you want a reply</span>
              </label>
              <input
                id="sf-email"
                type="email"
                className="sf-input"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                maxLength={200}
              />
            </div>

            {/* ─── Error + submit row ───────────────────────────────── */}
            {error && (
              <div className="sf-error" role="alert">
                {error}
              </div>
            )}

            <div className="sf-actions">
              {/* Cancel — returns to previous page (chat or splash, whichever
                  the user clicked Send feedback from). Round 4 (LG feedback). */}
              <button
                type="button"
                onClick={goBack}
                className="sf-btn-link"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="sf-btn-primary"
                disabled={submitting}
              >
                {submitting ? 'Sending...' : 'Submit feedback'}
              </button>
            </div>

            <p className="sf-privacy">
              No login required. Anonymous unless you share your email.
            </p>
          </form>
        ) : (
          <div className="sf-card sf-card-success">
            <div className="sf-success-icon" aria-hidden="true">✨</div>
            <h1 className="sf-card-title">Thank you!</h1>
            <p className="sf-card-subtitle">
              Your feedback was saved. It really does help — every response
              gets read by a human (me).
            </p>
            <div className="sf-actions sf-actions-success">
              <Link to="/" className="sf-btn-link">
                Back to home
              </Link>
              <Link to="/home" className="sf-btn-primary">
                Try LocalGenie →
              </Link>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default SiteFeedback;
